import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCategories, useOffers } from "../api/hooks";
import { OfferCard } from "../components/OfferCard";
import { GridSkeleton, ErrorState, EmptyState, categoryIcon } from "../components/States";
import { MaskedLine } from "../components/Motion";
import { Seo } from "../components/Seo";

const CategoryPage = () => {
    const { slug } = useParams();
    const { data: categories } = useCategories();
    const { data: offers, isLoading, isError, refetch } = useOffers({ category: slug });

    const category = categories?.find((c) => c.slug === slug);
    const Icon = categoryIcon(category?.icon);

    return (
        <>
            <Seo
                title={`${category?.name || "Category"} offers — SahiKhoj`}
                description={`Live ${category?.name || ""} deals, coupons and offers on SahiKhoj from supported platforms.`}
                path={`/categories/${slug}`}
                crumbs={[
                    { name: "Home", path: "/" },
                    { name: "Categories", path: "/categories" },
                    { name: category?.name || slug, path: `/categories/${slug}` },
                ]}
            />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <Link to="/categories" data-testid="category-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-mist transition-colors hover:text-saffron">
                        <ArrowLeft className="h-4 w-4" />
                        All categories
                    </Link>
                    <div className="mt-6 flex items-center gap-4">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-saffron">
                            <Icon className="h-6 w-6 text-white" />
                        </span>
                        <h1 className="font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                            <MaskedLine delay={0.15}>{category?.name || slug}</MaskedLine>
                        </h1>
                    </div>
                    {offers && (
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70" data-testid="category-offers-count">
                            {offers.length} live offer{offers.length === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
            </section>

            <section className="bg-cream py-14 sm:py-20" data-testid="category-page">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    {isLoading && <GridSkeleton count={8} />}
                    {isError && <ErrorState onRetry={refetch} />}
                    {offers && offers.length === 0 && (
                        <EmptyState
                            title={`No ${category?.name || ""} offers right now`}
                            message="New offers are added regularly — check back soon or browse another category."
                        />
                    )}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {(offers || []).map((o) => (
                            <OfferCard key={o.id} offer={o} />
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default CategoryPage;
